import React from 'react';
import _ from 'lodash';
import { Editor, EditorState, RichUtils, Modifier } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import '../../../../node_modules/draft-js/dist/Draft.css';
import { IconButton, Box, Tooltip, Divider, Typography, FormHelperText } from '@mui/material';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import Looks4Icon from '@mui/icons-material/Looks4';
import Looks5Icon from '@mui/icons-material/Looks5';
import Looks6Icon from '@mui/icons-material/Looks6';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';


const BLOCK_TYPES = [
  { label: 'H1', labelPl: 'H1', style: 'header-one', icon: <LooksOneIcon /> },
  { label: 'H2', labelPl: 'H2', style: 'header-two', icon: <LooksTwoIcon /> },
  { label: 'H3', labelPl: 'H3', style: 'header-three', icon: <Looks3Icon /> },
  { label: 'H4', labelPl: 'H4', style: 'header-four', icon: <Looks4Icon /> },
  { label: 'H5', labelPl: 'H5', style: 'header-five', icon: <Looks5Icon /> },
  { label: 'H6', labelPl: 'H6', style: 'header-six', icon: <Looks6Icon /> },
  { label: 'UL', labelPl: 'Lista punktowana', style: 'unordered-list-item', icon: <FormatListBulletedIcon /> },
  { label: 'OL', labelPl: 'Lista numerowana', style: 'ordered-list-item', icon: <FormatListNumberedIcon /> }
];


const INLINE_STYLES = [
  { label: 'Bold', labelPl: 'Pogrubienie', style: 'BOLD', icon: <FormatBoldIcon /> },
  { label: 'Italic', labelPl: 'Kursywa', style: 'ITALIC', icon: <FormatItalicIcon /> },
  { label: 'Underline', labelPl: 'Podkreślenie', style: 'UNDERLINE', icon: <FormatUnderlinedIcon /> },
  { label: 'red', labelPl: 'Czerwony', style: 'RED', icon: <FormatUnderlinedIcon /> },
  { label: 'green', labelPl: 'Zielony', style: 'GREEN', icon: <FormatUnderlinedIcon /> },
  { label: 'blue', labelPl: 'Niebieski', style: 'BLUE', icon: <FormatUnderlinedIcon /> }
];

const styleMap = {
  'RED': {
    'color': '#900'
  },
  'GREEN': {
    'color': '#007500'
  },
  'BLUE': {
    'color': '#00f'
  }
};

const options = {
  inlineStyles: {
    RED: { style: { color: '#900' } },
    GREEN: { style: { color: '#007500' } },
    BLUE: { style: { color: '#009' } }
  }
};

const optionsImport = {
  customInlineFn: (element, { Style }) => {
    if (element.style.color) {
      if (element.style.color === 'rgb(153, 0, 0)') {
        return Style('RED');
      }

      if (element.style.color === 'rgb(0, 117, 0)') {
        return Style('GREEN');
      }

      if (element.style.color === 'rgb(0, 0, 153)') {
        return Style('BLUE');
      }
    }
  }
};

const StyleButton = props => {
  const onClickButton = e => {
    e.preventDefault();
    props.onToggle(props.style);
  };

  if (props.label === 'red' || props.label === 'green' || props.label === 'blue') {
    return <button onMouseDown={e => {
      onClickButton(e);
      props.toggleColor(props.style);
    }} style={{
      width: '14px',
      height: '14px',
      margin: '8px',
      backgroundColor: styleMap[props.style].color,
      border: props.active ? '2px solid black' : `2px solid ${styleMap[props.style].color}`,
      borderRadius: '2px',
      cursor: 'pointer',
      boxSizing: 'border-box'
    }}></button>;
  }

  return <Tooltip title={props.labelPl}>
    <IconButton onMouseDown={onClickButton} color={props.active ? 'primary' : 'default'}>{props.icon}</IconButton>
  </Tooltip>;
};


const RichTextEditor = ({ text, formik, fieldName = 'email_message', label = 'Wiadomość E-mail', placeholder = 'Wiadomość E-mail', error = false, helperText = null, height = 'auto' }) => {

  const state = stateFromHTML(text, optionsImport);

  const [editorState, setEditorState] = React.useState(
    () => EditorState.createWithContent(state)
  );
  const lastEditorValue = React.useRef(text || '');
  const editor = React.useRef(null);

  React.useEffect(() => {
    if (text !== lastEditorValue.current) {
      const contentState = stateFromHTML(text || '', optionsImport);

      setEditorState(
        EditorState.createWithContent(contentState)
      );

      lastEditorValue.current = text || '';
    }
  }, [text]);

  function focusEditor() {
    editor.current.focus();
  }

  const BlockStyleControls = props => (
    <Box>
      {BLOCK_TYPES.map(type => (
        <StyleButton
          key={type.label}
          label={type.label}
          labelPl={type.labelPl}
          icon={type.icon}
          onToggle={props.onToggle}
          style={type.style}
          active={RichUtils.getCurrentBlockType(editorState) === type.style}
        />
      )
      )}
    </Box>
  );


  const InlineStyleControls = props => (
    <Box>
      {INLINE_STYLES.map(type => (
        <StyleButton
          key={type.label}
          label={type.label}
          labelPl={type.labelPl}
          icon={type.icon}
          onToggle={props.onToggle}
          style={type.style}
          active={editorState.getCurrentInlineStyle().has(type.style)}
          toggleColor={toggleColor}
        />
      ))}
    </Box>
  );

  const onInlineClick = e => {
    const nextState = RichUtils.toggleInlineStyle(editorState, e);
    setEditorState(nextState);
  };

  const onBlockClick = e => {
    const nextState = RichUtils.toggleBlockType(editorState, e);
    setEditorState(nextState);
  };

  const handleChange = newEditorState => {
    setEditorState(newEditorState);

    const html = stateToHTML(
      newEditorState.getCurrentContent(),
      options
    );

    lastEditorValue.current = html;

    formik.setFieldValue(fieldName, html);
  };

  const toggleColor = toggledColor => {
    const selection = editorState.getSelection();

    const nextContentState = Object.keys(styleMap)
      .reduce((contentState, color) => Modifier.removeInlineStyle(contentState, selection, color), editorState.getCurrentContent());

    let nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      'change-inline-style'
    );

    const currentStyle = editorState.getCurrentInlineStyle();

    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, color) => RichUtils.toggleInlineStyle(state, color), nextEditorState);
    }

    if (!currentStyle.has(toggledColor)) {
      nextEditorState = RichUtils.toggleInlineStyle(
        nextEditorState,
        toggledColor
      );
    }

    setEditorState(nextEditorState);

  };

  return (
    <>
      <Box>
        {!_.isNil(label)
          ? <>
            <Typography variant="subtitle1">{label}</Typography>
            <Divider />
          </>
          : null}
        <BlockStyleControls onToggle={onBlockClick} />
        <InlineStyleControls onToggle={onInlineClick} />
      </Box>
      <div
        style={{
          border: `1px solid ${error ? '#FF4842' : '#dce0e4'}`,
          borderRadius: '8px',
          padding: '16.5px 14px',
          cursor: 'text',
          maxHeight: '300px',
          height,
          overflow: 'auto'
        }}
        onClick={focusEditor}
      >
        <Editor
          ref={editor}
          customStyleMap={styleMap}
          editorState={editorState}
          onChange={handleChange}
          placeholder={placeholder}
        />
      </div>
      {error ? <FormHelperText style={{ color: '#FF4842' }}>{helperText}</FormHelperText > : null}
    </>
  );

};

export default RichTextEditor;
